import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ChevronDown, ChevronRight, RefreshCw, Loader2, Sparkles, Inbox } from "lucide-react";

const KEY = "vera_briefing_open";

// Riepilogo di apertura giornata: vive nell'area di Vera, chiuso di default.
export default function VeraBriefing() {
  const [open, setOpen] = useState(() => localStorage.getItem(KEY) !== "0");
  const [busy, setBusy] = useState(false);
  const { data: nuovi } = useQuery({
    queryKey: ["nuovi-documenti"],
    queryFn: async () => (await api.get("/dashboard/nuovi-documenti")).data,
    refetchInterval: 1000 * 60 * 5,
  });
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["briefing"],
    queryFn: async () => (await api.get("/dashboard/briefing")).data,
    staleTime: 1000 * 60 * 60 * 6,
  });

  const toggle = () => { const v = !open; setOpen(v); localStorage.setItem(KEY, v ? "1" : "0"); };
  const rigenera = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try { await api.get("/dashboard/briefing", { params: { refresh: true } }); await refetch(); }
    finally { setBusy(false); }
  };

  const b = data || {};
  const scadenze = (b.scadenze_step || []).length + (b.documenti_in_scadenza || []).length;
  const ferme = (b.pratiche_ferme || []).length;

  return (
    <div data-testid="vera-briefing" className="border-b border-[#1A2B4A]/10 bg-[#fffdf7]">
      <button data-testid="vera-briefing-toggle" onClick={toggle}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-[#faf5e6] transition-colors">
        {open ? <ChevronDown className="w-3.5 h-3.5 text-[#8a6d1f] shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-[#8a6d1f] shrink-0" />}
        <Sparkles className="w-3.5 h-3.5 text-[#C9A84C] shrink-0" />
        <span className="text-[11px] uppercase tracking-[0.12em] text-[#8a6d1f]">Vera informa</span>
        <span className="ml-auto text-[11px] text-[#4a5568] shrink-0" data-testid="vera-briefing-teaser">
          {isLoading ? "…" : `${scadenze} scadenze · ${ferme} ferme${nuovi?.totale ? ` · ${nuovi.totale} nuovi doc.` : ""}`}
        </span>
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1" data-testid="vera-briefing-body">
          {isLoading ? (
            <p className="text-xs text-[#4a5568] flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-[#C9A84C]" /> Sto preparando il riepilogo…
            </p>
          ) : (
            <>
              {nuovi?.totale > 0 && (
                <Link to="/" data-testid="vera-briefing-nuovi-doc"
                  className="mb-3 flex items-center gap-2 text-xs text-[#8a6d1f] bg-white border border-[#C9A84C]/50 rounded-sm px-2.5 py-2 hover:bg-[#faf5e6] transition-colors">
                  <Inbox className="w-3.5 h-3.5 shrink-0" />
                  <span>{nuovi.totale} nuovo/i documento/i inviato/i dai clienti dal portale — apri la Dashboard</span>
                </Link>
              )}
              <p className="text-xs text-[#1A2B4A] leading-relaxed whitespace-pre-line" data-testid="vera-briefing-sintesi">
                {b.sintesi || "Nessun riepilogo disponibile."}
              </p>
              {(b.azioni || []).length > 0 && (
                <ul className="mt-3 space-y-1.5" data-testid="vera-briefing-azioni">
                  {b.azioni.map((a, i) => (
                    <li key={a} data-testid={`vera-briefing-azione-${i}`}
                      className="text-xs text-[#1A2B4A] border-l-2 border-[#C9A84C] pl-2.5">{a}</li>
                  ))}
                </ul>
              )}
              {ferme > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(b.pratiche_ferme || []).slice(0, 3).map((p) => (
                    <Link key={p.pratica_id} to={`/pratiche/${p.pratica_id}`} data-testid={`vera-briefing-ferma-${p.pratica_id}`}
                      className="text-[11px] text-[#1A2B4A] bg-white border border-[#1A2B4A]/12 rounded-md px-2.5 py-1 hover:border-[#C9A84C] transition-colors">
                      {p.cliente}
                    </Link>
                  ))}
                </div>
              )}
              <button data-testid="vera-briefing-refresh" onClick={rigenera} disabled={busy}
                className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-[#8a6d1f] hover:text-[#1A2B4A] transition-colors disabled:opacity-50">
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Aggiorna
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
